import { Node } from '@xyflow/react';
import { ServiceData, ActorData, ZoneData, KafkaTopicData } from '../types/diagram.types';
import { SERVICE_ENDPOINTS } from './serviceEndpoints';

// Zone background nodes (rendered behind services)
export const ZONE_NODES: Node<ZoneData>[] = [
  {
    id: 'zone-external',
    type: 'zone',
    position: { x: 0, y: 0 },
    style: { width: 2200, height: 220, zIndex: -10 },
    data: { label: 'Внешний', zoneId: 'zone-external', color: 'rgba(158,158,158,0.07)', borderColor: '#9E9E9E' },
    draggable: false,
    selectable: true,
  },
  {
    id: 'zone-dmz',
    type: 'zone',
    position: { x: 0, y: 240 },
    style: { width: 2200, height: 200, zIndex: -10 },
    data: { label: 'DMZ', zoneId: 'zone-dmz', color: 'rgba(255,109,0,0.07)', borderColor: '#FF6D00' },
    draggable: false,
    selectable: true,
  },
  {
    id: 'zone-kafka',
    type: 'zone',
    position: { x: 0, y: 460 },
    style: { width: 2200, height: 150, zIndex: -10 },
    data: { label: 'Kafka — Z5 (Message Broker)', zoneId: 'zone-kafka', color: 'rgba(255,143,0,0.07)', borderColor: '#FF8F00' },
    draggable: false,
    selectable: true,
  },
  {
    id: 'zone-internal',
    type: 'zone',
    position: { x: 0, y: 640 },
    style: { width: 2200, height: 700, zIndex: -10 },
    data: { label: 'Внутренний', zoneId: 'zone-internal', color: 'rgba(21,101,192,0.07)', borderColor: '#1565C0' },
    draggable: false,
    selectable: true,
  },
  {
    id: 'zone-partners',
    type: 'zone',
    position: { x: 0, y: 1360 },
    style: { width: 2200, height: 200, zIndex: -10 },
    data: { label: 'Партнёры', zoneId: 'zone-partners', color: 'rgba(106,27,154,0.07)', borderColor: '#6A1B9A' },
    draggable: false,
    selectable: true,
  },
];

// Actor nodes (external participants)
export const ACTOR_NODES: Node<ActorData>[] = [
  { id: 'act-mobile', type: 'actor', position: { x: 100, y: 60 }, data: { label: 'Mobile App', name: 'Mobile App (iOS/Android)', actorType: 'client', zone: 'zone-external' } },
  { id: 'act-web', type: 'actor', position: { x: 320, y: 60 }, data: { label: 'Web App', name: 'Web App', actorType: 'client', zone: 'zone-external' } },
  { id: 'act-admin-panel', type: 'actor', position: { x: 540, y: 60 }, data: { label: 'Admin Panel', name: 'Admin Panel (браузер)', actorType: 'client', zone: 'zone-external' } },
  { id: 'act-binance', type: 'actor', position: { x: 800, y: 60 }, data: { label: 'Binance LP', name: 'Binance Liquidity Provider', actorType: 'lp', zone: 'zone-external' } },
  { id: 'act-kraken', type: 'actor', position: { x: 1000, y: 60 }, data: { label: 'Kraken LP', name: 'Kraken Liquidity Provider', actorType: 'lp', zone: 'zone-external' } },
  { id: 'act-blockchain', type: 'actor', position: { x: 1200, y: 60 }, data: { label: 'Blockchain Nodes', name: 'Bitcoin / Ethereum / TRON Nodes', actorType: 'blockchain', zone: 'zone-external' } },
  { id: 'act-sendgrid', type: 'actor', position: { x: 1450, y: 60 }, data: { label: 'SendGrid', name: 'SendGrid Email', actorType: 'notification', zone: 'zone-external' } },
  { id: 'act-twilio', type: 'actor', position: { x: 1640, y: 60 }, data: { label: 'Twilio SMS', name: 'Twilio SMS', actorType: 'notification', zone: 'zone-external' } },
  { id: 'act-fcm', type: 'actor', position: { x: 1820, y: 60 }, data: { label: 'FCM Push', name: 'Firebase FCM', actorType: 'notification', zone: 'zone-external' } },
  // Partners zone actors
  { id: 'act-fenige-psp', type: 'actor', position: { x: 200, y: 1410 }, data: { label: 'Fenige PSP', name: 'Fenige Payment System', actorType: 'psp', zone: 'zone-partners' } },
  { id: 'act-fusepay-psp', type: 'actor', position: { x: 500, y: 1410 }, data: { label: 'FusePay PSP', name: 'FusePay', actorType: 'psp', zone: 'zone-partners' } },
  { id: 'act-kyc-prov', type: 'actor', position: { x: 800, y: 1410 }, data: { label: 'KYC Provider', name: 'KYC / Identity Provider', actorType: 'compliance', zone: 'zone-partners' } },
  { id: 'act-aml-prov', type: 'actor', position: { x: 1050, y: 1410 }, data: { label: 'AML Provider', name: 'AML / Sanctions Provider', actorType: 'compliance', zone: 'zone-partners' } },
];

// Service nodes
export const SERVICE_NODES: Node<ServiceData>[] = [
  // DMZ
  {
    id: 'svc-001',
    type: 'service',
    position: { x: 400, y: 300 },
    data: { label: 'API Gateway', name: 'API Gateway', group: 'Gateways', zone: 'zone-dmz', techStack: ['Go', 'Nginx'], databases: [], status: 'active', repository: 'api-gateway', docFile: 'gateways/api-gateway.md' },
  },
  {
    id: 'svc-002',
    type: 'service',
    position: { x: 900, y: 300 },
    data: { label: 'Admin API', name: 'Admin API', group: 'Gateways', zone: 'zone-dmz', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'admin-api', docFile: 'gateways/admin-api.md' },
  },
  // Auth/User
  {
    id: 'svc-003',
    type: 'service',
    position: { x: 100, y: 700 },
    data: { label: 'Users Service', name: 'Users Service', group: 'Auth/User', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'users', docFile: 'services/01-users.md' },
  },
  {
    id: 'svc-025',
    type: 'service',
    position: { x: 320, y: 700 },
    data: { label: 'Profiles', name: 'Profiles (Admin Operators)', group: 'Auth/User', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'admin-api', docFile: 'services/25-profiles.md' },
  },
  // Finance
  {
    id: 'svc-004',
    type: 'service',
    position: { x: 100, y: 860 },
    data: { label: 'Accounts', name: 'Accounts Service', group: 'Finance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'accounts', docFile: 'services/02-accounts.md' },
  },
  {
    id: 'svc-005',
    type: 'service',
    position: { x: 320, y: 860 },
    data: { label: 'Operations', name: 'Operations Service', group: 'Finance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'operations', docFile: 'services/03-operations.md' },
  },
  {
    id: 'svc-020',
    type: 'service',
    position: { x: 540, y: 860 },
    data: { label: 'Limiter', name: 'Limiter Service', group: 'Finance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL', 'Redis'], status: 'active', repository: 'limiter', docFile: 'services/20-limiter.md' },
  },
  {
    id: 'svc-021',
    type: 'service',
    position: { x: 760, y: 860 },
    data: { label: 'Treasury', name: 'Treasury Service', group: 'Finance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'treasury', docFile: 'services/21-treasury.md' },
  },
  {
    id: 'svc-022',
    type: 'service',
    position: { x: 980, y: 860 },
    data: { label: 'Cards', name: 'Cards Service', group: 'Finance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'cards', docFile: 'services/22-cards.md' },
  },
  // Trading
  {
    id: 'svc-006',
    type: 'service',
    position: { x: 100, y: 1050 },
    data: { label: 'Orders', name: 'Orders Service', group: 'Trading', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'orders', docFile: 'services/04-orders.md' },
  },
  {
    id: 'svc-007',
    type: 'service',
    position: { x: 320, y: 1050 },
    data: { label: 'Orderbook', name: 'Orderbook Service', group: 'Trading', zone: 'zone-internal', techStack: ['Go'], databases: ['Redis'], status: 'active', repository: 'orderbook', docFile: 'services/05-orderbook-matcher.md' },
  },
  {
    id: 'svc-008',
    type: 'service',
    position: { x: 540, y: 1050 },
    data: { label: 'Matcher', name: 'Matcher Service', group: 'Trading', zone: 'zone-internal', techStack: ['Go'], databases: ['Redis'], status: 'active', repository: 'matcher', docFile: 'services/05-orderbook-matcher.md' },
  },
  {
    id: 'svc-009',
    type: 'service',
    position: { x: 760, y: 1050 },
    data: { label: 'Market Maker', name: 'Market Maker', group: 'Trading', zone: 'zone-internal', techStack: ['Go'], databases: ['Redis', 'PostgreSQL'], status: 'active', repository: 'market-maker', docFile: 'services/10-market-maker.md' },
  },
  {
    id: 'svc-010',
    type: 'service',
    position: { x: 980, y: 1050 },
    data: { label: 'Quick Exchange', name: 'Quick Exchange', group: 'Quick Exchange', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'quick-exchange', docFile: 'services/06-quick-exchange.md' },
  },
  // Market Data
  {
    id: 'svc-011',
    type: 'service',
    position: { x: 1200, y: 700 },
    data: { label: 'Market Data', name: 'Market Data Service', group: 'Market Data', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL', 'TimescaleDB'], status: 'active', repository: 'market-data', docFile: 'services/09-market-data.md' },
  },
  {
    id: 'svc-012',
    type: 'service',
    position: { x: 1420, y: 700 },
    data: { label: 'Courses', name: 'Courses Service (Exchange Rates)', group: 'Market Data', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL', 'TimescaleDB'], status: 'active', repository: 'courses', docFile: 'services/07-courses.md' },
  },
  {
    id: 'svc-013',
    type: 'service',
    position: { x: 1640, y: 700 },
    data: { label: 'Liquidity Providers', name: 'Liquidity Providers Service', group: 'Market Data', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL', 'TimescaleDB'], status: 'active', repository: 'liquidity-providers', docFile: 'services/08-liquidity-providers.md' },
  },
  // Acquiring
  {
    id: 'svc-014',
    type: 'service',
    position: { x: 1200, y: 880 },
    data: { label: 'Acquiring Gateway', name: 'Acquiring Gateway', group: 'Acquiring', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'acquiring/gateway', docFile: 'services/11-acquiring-gateway.md' },
  },
  {
    id: 'svc-015',
    type: 'service',
    position: { x: 1420, y: 880 },
    data: { label: 'Acq. Processing', name: 'Acquiring Processing', group: 'Acquiring', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'acquiring/processing', docFile: 'services/12-acquiring-processing.md' },
  },
  {
    id: 'svc-016',
    type: 'service',
    position: { x: 1640, y: 880 },
    data: { label: 'Acq. Sender', name: 'Acquiring Sender', group: 'Acquiring', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'acquiring-sender', docFile: 'services/13-acquiring-sender.md' },
  },
  {
    id: 'svc-017',
    type: 'service',
    position: { x: 1200, y: 1050 },
    data: { label: 'Fenige Adapter', name: 'Fenige Adapter', group: 'Acquiring', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'acquiring/fenige_adapter', docFile: 'services/14-fenige-adapter.md' },
  },
  {
    id: 'svc-018',
    type: 'service',
    position: { x: 1420, y: 1050 },
    data: { label: 'FusePay', name: 'FusePay Service', group: 'Acquiring', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'fuse-pay-service', docFile: 'services/15-fuse-pay.md' },
  },
  // Crypto
  {
    id: 'svc-019',
    type: 'service',
    position: { x: 1680, y: 1050 },
    data: { label: 'Crypto Acquiring', name: 'Crypto Acquiring', group: 'Crypto', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'crypto-acquiring', docFile: 'services/16-crypto-acquiring.md' },
  },
  {
    id: 'svc-020c',
    type: 'service',
    position: { x: 1900, y: 1050 },
    data: { label: 'Crypto Gateway', name: 'Crypto Gateway', group: 'Crypto', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'crypto-gateway', docFile: 'services/17-crypto-gateway.md' },
  },
  // Compliance
  {
    id: 'svc-021k',
    type: 'service',
    position: { x: 100, y: 1240 },
    data: { label: 'KYC', name: 'KYC Service', group: 'Compliance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL', 'S3'], status: 'active', repository: 'kyc', docFile: 'services/18-kyc.md' },
  },
  {
    id: 'svc-022s',
    type: 'service',
    position: { x: 320, y: 1240 },
    data: { label: 'Scoring', name: 'Scoring Service', group: 'Compliance', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'scoring', docFile: 'services/19-scoring.md' },
  },
  // Notifications
  {
    id: 'svc-023',
    type: 'service',
    position: { x: 540, y: 1240 },
    data: { label: 'Notifications', name: 'Notifications Service', group: 'Notifications', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'notifications', docFile: 'services/23-notifications.md' },
  },
  {
    id: 'svc-024',
    type: 'service',
    position: { x: 760, y: 1240 },
    data: { label: 'Sender', name: 'Sender Service', group: 'Notifications', zone: 'zone-internal', techStack: ['Go'], databases: ['PostgreSQL'], status: 'active', repository: 'sender', docFile: 'services/24-sender.md' },
  },
  // Support / новые сервисы (перенесены внутрь контура, группа services/*)
  {
    id: 'svc-026',
    type: 'service',
    position: { x: 980, y: 1240 },
    data: { label: 'Support Chat', name: 'Support Chat (widget + operator + Telegram)', group: 'Support', zone: 'zone-internal', techStack: ['Go', 'WS', 'Vite'], databases: ['PostgreSQL'], status: 'active', repository: 'support-chat', docFile: 'services/26-support-chat.md' },
  },
  {
    id: 'svc-027',
    type: 'service',
    position: { x: 1200, y: 1240 },
    data: { label: 'welID', name: 'welID (KYC/AML провайдер, бывш. sumsub-mock)', group: 'Compliance', zone: 'zone-internal', techStack: ['Python', 'FastAPI'], databases: ['PostgreSQL'], status: 'active', repository: 'welID', docFile: 'services/27-welid.md' },
  },
];

// Inject endpoint data from SERVICE_ENDPOINTS into each service node
SERVICE_NODES.forEach(node => {
  if (SERVICE_ENDPOINTS[node.id]) {
    node.data.endpoints = SERVICE_ENDPOINTS[node.id];
  }
});

// Kafka Broker container node — lives in zone-kafka (Z5) between DMZ and Internal
// Must come BEFORE topic nodes in array for parent/child resolution
export const KAFKA_BROKER_NODE: Node = {
  id: 'kafka-broker',
  type: 'kafkaBroker',
  position: { x: 705, y: 466 },
  style: { width: 950, height: 26, zIndex: -5 },
  data: { zone: 'zone-kafka' },
  selectable: true,
};

// Kafka topic nodes — children of kafka-broker (move with header), no extent constraint
export const KAFKA_TOPIC_NODES: Node<KafkaTopicData>[] = [
  {
    id: 'kafka-user-registered',
    type: 'kafkaTopic',
    position: { x: 8, y: 32 },
    parentId: 'kafka-broker',
    data: { label: 'USER_REGISTERED', topicName: 'USER_REGISTERED', zone: 'zone-kafka', publisherCount: 1, subscriberCount: 1 },
  },
  {
    id: 'kafka-kyc-approved',
    type: 'kafkaTopic',
    position: { x: 164, y: 32 },
    parentId: 'kafka-broker',
    data: { label: 'KYC_APPROVED', topicName: 'KYC_APPROVED', zone: 'zone-kafka', publisherCount: 1, subscriberCount: 1 },
  },
  {
    id: 'kafka-trade-executed',
    type: 'kafkaTopic',
    position: { x: 320, y: 32 },
    parentId: 'kafka-broker',
    data: { label: 'TRADE_EXECUTED', topicName: 'TRADE_EXECUTED', zone: 'zone-kafka', publisherCount: 1, subscriberCount: 1 },
  },
  {
    id: 'kafka-mm-filled',
    type: 'kafkaTopic',
    position: { x: 476, y: 32 },
    parentId: 'kafka-broker',
    data: { label: 'MM_ORDER_FILLED', topicName: 'MM_ORDER_FILLED', zone: 'zone-kafka', publisherCount: 1, subscriberCount: 1 },
  },
  {
    id: 'kafka-rate-updated',
    type: 'kafkaTopic',
    position: { x: 632, y: 32 },
    parentId: 'kafka-broker',
    data: { label: 'RATE_UPDATED', topicName: 'RATE_UPDATED', zone: 'zone-kafka', publisherCount: 1, subscriberCount: 2 },
  },
  {
    id: 'kafka-transaction-requests',
    type: 'kafkaTopic',
    position: { x: 788, y: 32 },
    parentId: 'kafka-broker',
    data: { label: 'TRANSACTION_REQUESTS', topicName: 'transaction_requests', zone: 'zone-kafka', publisherCount: 1, subscriberCount: 1 },
  },
];

export const ALL_INITIAL_NODES: Node[] = [
  ...ZONE_NODES,
  ...ACTOR_NODES,
  ...SERVICE_NODES,
  KAFKA_BROKER_NODE,      // parent must come before children
  ...KAFKA_TOPIC_NODES,
];
